// src/layout/components/finance/PerfYearGroupedBars.jsx
import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const BR_TZ = "America/Sao_Paulo";

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function fmtCompact(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "";
  const a = Math.abs(x);
  if (a >= 1_000_000) return `${(x / 1_000_000).toFixed(Math.abs(x / 1_000_000) >= 10 ? 0 : 1)}M`;
  if (a >= 1_000) return `${(x / 1_000).toFixed(Math.abs(x / 1_000) >= 10 ? 0 : 1)}k`;
  if (Number.isInteger(x)) return String(x);
  return x.toFixed(1);
}

function monthLabelFromIndex(mi) {
  const m = Number(mi);
  if (!Number.isFinite(m) || m < 1 || m > 12) return "";
  const d = new Date(Date.UTC(2024, m - 1, 1));
  return d.toLocaleDateString("pt-BR", { timeZone: BR_TZ, month: "short" }).replace(".", "");
}

function tooltipFormatter(value, name) {
  if (value == null) return ["—", name];
  const n = Number(value);
  if (!Number.isFinite(n)) return ["—", name];
  return [fmtCompact(n), name];
}

/**
 * PerfYearGroupedBars (simplificado)
 *
 * items esperado: [{ month_index: 1..12, net_month_cur, net_month_py }]
 * - curMonthIndex: 1..12 (mês atual)
 *
 * Regras:
 * - PY aparece para 12 meses (null vira 0)
 * - CUR aparece até curMonthIndex (mês atual pode estar aberto), tudo na mesma cor
 * - meses futuros: null (não plota)
 */
export default function PerfYearGroupedBars({ items = [], theme, height = 240, curMonthIndex }) {
  const t = theme || {};
  const curMi = Number(curMonthIndex || new Date().getMonth() + 1);

  const colorCur = t?.chartPrimary ?? "rgba(0,200,200,0.95)";     // ano corrente
  const colorPY  = t?.chartSecondary ?? "rgba(120,180,255,0.85)"; // ano anterior

  const grid = "rgba(255,255,255,0.08)";
  const axis = "rgba(255,255,255,0.16)";
  const tick = "rgba(255,255,255,0.65)";

  const data = useMemo(() => {
    const map = new Map();
    for (const r of Array.isArray(items) ? items : []) {
      const mi = safeNum(r?.month_index);
      if (!Number.isFinite(mi)) continue;
      map.set(mi, {
        py: safeNum(r?.net_month_py),
        cur: safeNum(r?.net_month_cur),
      });
    }

    const out = [];
    for (let m = 1; m <= 12; m++) {
      const r = map.get(m) || {};
      const py = r?.py == null ? 0 : r.py;            // PY sempre existe (barra aparece)
      const cur = r?.cur == null ? 0 : r.cur;         // se não vier, 0

      out.push({
        month_index: m,
        label: monthLabelFromIndex(m),
        net_month_py: py,
        net_month_cur: m <= curMi ? cur : null,       // meses futuros somem
      });
    }
    return out;
  }, [items, curMi]);

  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 10, left: 6, bottom: 6 }}>
          <CartesianGrid stroke={grid} strokeDasharray="4 6" />

          <XAxis
            dataKey="label"
            tick={{ fill: tick, fontSize: 12 }}
            axisLine={{ stroke: axis }}
            tickLine={{ stroke: axis }}
            interval={0}
          />

          <YAxis
            width={52}
            tick={{ fill: tick, fontSize: 12 }}
            axisLine={{ stroke: axis }}
            tickLine={{ stroke: axis }}
            tickFormatter={fmtCompact}
          />

          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={{
              background: "rgba(0,0,0,0.85)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 12,
              color: t?.text ?? "white",
            }}
            labelStyle={{ color: t?.muted ?? "rgba(255,255,255,0.65)" }}
            cursor={{ fill: "rgba(255,255,255,0.06)" }}
          />

          <Legend wrapperStyle={{ fontSize: 12, color: tick }} />

          {/* Ano anterior */}
          <Bar
            name="Ano anterior"
            dataKey="net_month_py"
            fill={colorPY}
            radius={[8, 8, 0, 0]}
            isAnimationActive={false}
          />

          {/* Ano corrente (uma cor só) */}
          <Bar
            name="Ano (corrente)"
            dataKey="net_month_cur"
            fill={colorCur}
            radius={[8, 8, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
